import { useState, useEffect } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import api, { getConsultation, getPatient } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Stethoscope,
  Scale,
  Brain,
  Pill,
  Calendar,
  Loader2,
  AlertCircle,
  ClipboardList,
} from "lucide-react";

const safe = (v, fallback = "—") => (v != null && v !== "" ? String(v) : fallback);
const safeObj = (o) => (o && typeof o === "object" ? o : {});

export default function ConsultationView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const modoAula = new URLSearchParams(location.search).get("modo") === "aula";
  const [consultation, setConsultation] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setError("ID de consulta no válido");
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    setConsultation(null);
    setPatient(null);

    const fetchData = async () => {
      try {
        const consultRes = await getConsultation(id);
        const c = consultRes?.data ?? consultRes;
        if (!alive) return;
        if (!c) {
          setError("Consulta no encontrada");
          return;
        }
        setConsultation(c);
        const pid = c?.paciente_id;
        if (pid) {
          try {
            const pRes = await getPatient(pid);
            const p = pRes?.data ?? pRes;
            if (alive && p) setPatient(p);
          } catch {
            if (alive) toast.error("No se pudo cargar la ficha del paciente");
          }
        }
      } catch {
        if (alive) {
          setError("Error al cargar la consulta");
          toast.error("No se pudo cargar la consulta");
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    return () => {
      alive = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card className="border-slate-200 shadow-sm bg-white">
          <CardContent className="pt-10 pb-10 flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-sky-600" aria-hidden />
            <p className="text-sm font-medium text-slate-800">Cargando consulta…</p>
            <p className="text-xs text-slate-500">Recuperando los datos de esta visita.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !consultation) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 px-4">
        <Card className="border-slate-200 bg-white shadow-sm">
          <CardContent className="pt-8 pb-8 px-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 text-left">
              <AlertCircle className="h-10 w-10 text-amber-500 shrink-0" aria-hidden />
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-slate-900">No se pudo cargar la consulta</h1>
                <p className="text-sm text-slate-600 mt-2">{error || "Consulta no encontrada"}</p>
                <div className="flex flex-wrap gap-2 mt-6">
                  <Button variant="outline" onClick={() => navigate(-1)}>
                    Volver
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to="/pacientes">Ir a pacientes</Link>
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ESCALA_ETIQUETA = {
    zarit: "Zarit (Sobrecarga del cuidador)",
    tug: "TUG (Timed Up and Go)",
    sarc_f: "SARC-F",
    moca: "MoCA (Montreal Cognitive Assessment)",
    mini_cog: "Mini-Cog",
    cam: "CAM (Confusion Assessment Method)",
    phq9: "PHQ-9",
    gad7: "GAD-7",
    cfs: "Clinical Frailty Scale (CFS)",
    charlson: "Índice de Charlson",
    eva_dolor: "EVA (Escala Visual Análoga)",
    sppb: "SPPB (Short Physical Performance Battery)",
    polifarmacia_simple: "Polifarmacia simple",
  };

  const c = consultation;
  const vitals = safeObj(c.vitals);
  const escalas = safeObj(c.escalas);
  const ant = safeObj(c.antecedentes);
  const fecha = c?.fecha_consulta
    ? new Date(c.fecha_consulta).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })
    : "—";
  const pacienteNombre = patient ? `${patient.nombres || ""} ${patient.apellidos || ""}`.trim() || "Paciente" : c.paciente_id || "Paciente";

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to={c.paciente_id ? `/pacientes/${c.paciente_id}` : "/pacientes"}>
          <Button variant="ghost" size="icon" title="Volver al paciente">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            type="button"
            size="sm"
            className="bg-sky-600 hover:bg-sky-700 text-white"
            onClick={async () => {
              if (modoAula) {
                try {
                  const response = await api.get(`/pdf/aula/practica/${id}`, { responseType: "blob" });
                  const blob = new Blob([response.data], { type: "application/pdf" });
                  const url = URL.createObjectURL(blob);
                  window.open(url, "_blank", "noopener,noreferrer");
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch {
                  toast.error("No se pudo generar el PDF de práctica");
                }
              } else {
                navigate(`/entregable/${id}`);
              }
            }}
          >
            Generar documento
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-slate-200 text-slate-500"
            disabled
            title="La edición de consultas ya guardadas estará disponible próximamente. Cree una nueva consulta desde el paciente."
          >
            Editar (próximamente)
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-sky-600" />
            Consulta · {safe(c.tipo_consulta, "Consulta")}
          </CardTitle>
          <p className="text-sm text-slate-500">
            {fecha} · {pacienteNombre}
          </p>
        </CardHeader>
      </Card>

      {/* Motivo de consulta */}
      {(c.motivo_consulta || c.historia_enfermedad_actual) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-sky-600" /> Motivo de consulta
            </CardTitle>
          </CardHeader>
          <CardContent>
            {c.motivo_consulta && <p className="text-sm text-slate-700 mb-2">{c.motivo_consulta}</p>}
            {c.tiempo_evolucion && <p className="text-xs text-slate-500">Evolución: {c.tiempo_evolucion}</p>}
            {c.historia_enfermedad_actual && (
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{c.historia_enfermedad_actual}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Antecedentes */}
      {(ant.patologicos || ant.quirurgicos || ant.no_patologicos || ant.familiares || (c.medicamentos_actuales || []).length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-sky-600" /> Antecedentes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-700">
            {ant.patologicos && (
              <p>
                <strong>Patológicos:</strong> {ant.patologicos}
              </p>
            )}
            {ant.quirurgicos && (
              <p>
                <strong>Quirúrgicos:</strong> {ant.quirurgicos}
              </p>
            )}
            {ant.no_patologicos && (
              <p>
                <strong>No patológicos:</strong> {ant.no_patologicos}
              </p>
            )}
            {ant.familiares && (
              <p>
                <strong>Familiares:</strong> {ant.familiares}
              </p>
            )}
            {(c.medicamentos_actuales || []).length > 0 && (
              <p>
                <strong>Medicamentos actuales:</strong> {(c.medicamentos_actuales || []).join(", ")}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Examen físico / Signos vitales */}
      {(vitals.sbp ||
        vitals.dbp ||
        vitals.hr ||
        vitals.weight ||
        vitals.height ||
        vitals.bmi ||
        vitals.waist_circ ||
        vitals.calf_circ ||
        vitals.arm_circ ||
        c.examen_clinico) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Stethoscope className="h-4 w-4 text-sky-600" /> Examen físico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-700">
            {(vitals.sbp || vitals.dbp) && (
              <p>
                PA: {vitals.sbp}/{vitals.dbp} mmHg
              </p>
            )}
            {vitals.hr && <p>FC: {vitals.hr} lpm</p>}
            {vitals.weight && <p>Peso: {vitals.weight} kg</p>}
            {vitals.height && <p>Talla: {vitals.height} cm</p>}
            {vitals.bmi && <p>IMC: {vitals.bmi}</p>}
            {vitals.waist_circ && <p>Circunferencia abdominal: {vitals.waist_circ} cm</p>}
            {vitals.calf_circ && <p>Circunferencia pantorrilla: {vitals.calf_circ} cm</p>}
            {vitals.arm_circ && <p>Circunferencia brazo: {vitals.arm_circ} cm</p>}
            {c.examen_clinico && <div className="pt-2 whitespace-pre-wrap">{c.examen_clinico}</div>}
          </CardContent>
        </Card>
      )}

      {/* Escalas */}
      {Object.keys(escalas).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Scale className="h-4 w-4 text-sky-600" /> Evaluaciones registradas en esta consulta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-slate-500 mb-3">
              Información registrada en el momento de la consulta. No ha sido recalculada.
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(escalas).map(([k, v]) => {
                const score = v && typeof v === "object" ? (v.value ?? v.puntaje) : v;
                const interp = v && typeof v === "object" ? v.interpretacion : null;
                if (score === null || score === undefined) return null;
                const scoreLabel =
                  k === "cam" || k === "mini_cog"
                    ? score === true || score === 1
                      ? "Positivo"
                      : score === false || score === 0
                        ? "Negativo"
                        : String(score)
                    : score;
                return (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-slate-100 border border-slate-200 text-sm"
                  >
                    <span className="font-medium">{ESCALA_ETIQUETA[k] || k}:</span> {scoreLabel}
                    {k === "tug" && <span className="text-slate-500 text-xs"> s</span>}
                    {k === "polifarmacia_simple" && <span className="text-slate-500 text-xs"> medicamentos</span>}
                    {interp && <span className="text-slate-500 text-xs"> ({interp})</span>}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Impresión clínica / Síndromes */}
      {(c.impresion_clinica || c.sindrome_geriatrico) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="h-4 w-4 text-sky-600" /> Impresión clínica
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-700">
            {c.impresion_clinica && <p className="whitespace-pre-wrap">{c.impresion_clinica}</p>}
            {c.sindrome_geriatrico && (
              <p className="text-amber-700">
                <strong>Síndromes:</strong> {c.sindrome_geriatrico}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diagnósticos */}
      {c.diagnostico && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4 text-sky-600" /> Diagnósticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.diagnostico}</p>
          </CardContent>
        </Card>
      )}

      {/* Plan terapéutico */}
      {(c.plan_terapeutico || c.nutrition_plan || c.non_pharm_plan || (c.medicamentos_indicados || []).length > 0) && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Pill className="h-4 w-4 text-sky-600" /> Plan terapéutico
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-slate-700">
            {(c.medicamentos_indicados || []).length > 0 && (
              <div>
                <strong>Medicamentos indicados:</strong>
                <ul className="list-disc list-inside mt-1">
                  {(c.medicamentos_indicados || []).map((m, i) => (
                    <li key={i}>
                      {m?.nombre || m?.nombre_medicamento || JSON.stringify(m)} {m?.dosis && `- ${m.dosis}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {c.plan_terapeutico && <p className="whitespace-pre-wrap">{c.plan_terapeutico}</p>}
            {c.nutrition_plan && (
              <p>
                <strong>Plan nutricional:</strong> {c.nutrition_plan}
              </p>
            )}
            {c.non_pharm_plan && (
              <p>
                <strong>Plan no farmacológico:</strong> {c.non_pharm_plan}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Próxima cita */}
      {c.proxima_cita && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-sky-600" /> Próxima cita
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">
              {c.proxima_cita} {c.proxima_cita_hora ? `· ${c.proxima_cita_hora}` : ""}
            </p>
          </CardContent>
        </Card>
      )}

      <Separator className="my-2 bg-sky-100" />

      <p className="text-center text-xs text-slate-500">
        Para imprimir o compartir, use <strong className="text-slate-700">Generar documento</strong>.
      </p>
    </div>
  );
}
